import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteProps } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AuthContext from "../common/authentication/AuthLogin";
import { getFCMToken } from "../firebase";
import {
  setIsDeviceRegistered,
  setProfile,
  setPermissions,
  setAssignedLocations,
  setCurrentLocation,
  setLoadingUserData,
  setPersistedNavbarProviders,
  setPersistedNurseDefaultsLocation,
  setPersistedNurseCareTeam,
  logout,
} from "../redux/slices/auth.slice";
import { RootState } from "../redux/store/store";
import {
  usePushNotificationControllerServiceSendFcmNotification,
  useUserControllerServiceGetProfile,
  useUserControllerServiceGetAssignedLocations,
  useUserControllerServiceRevokeDeviceToken,
} from "../services/queries";
import { CenteredLoader } from "../common/components/loader";
import { setStripeCustomerId } from "../redux/slices/stripe/stripe.slice";
import { connectSocket, disconnectSocket } from "../socket";
import useHttp from "../common/hooks/useHttp";
import { BASE_URL } from "../RestConstant";

function PrivateRoute({ children }: RouteProps): JSX.Element {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isDeviceRegistered, isLoadingUserData } = useSelector(
    (store: RootState) => store.auth,
  );
  const shouldRefetchProfile = useSelector(
    (state: RootState) => state.auth.shouldRefetchProfile,
  );
  const persistedNavbarProviders = useSelector(
    (state: RootState) => state.auth.persistedNavbarProviders,
  );
  const persistedNurseDefaultsLocation = useSelector(
    (state: RootState) => state.auth.persistedNurseDefaultsLocation,
  );
  const persistedNurseCareTeam = useSelector(
    (state: RootState) => state.auth.persistedNurseCareTeam,
  );

  const auth = useContext(AuthContext);
  const isLoggedIn = auth.authenticated;
  const previousAuthState = useRef<boolean>(false);

  const {
    data,
    refetch,
    isLoading: isProfileLoading,
  } = useUserControllerServiceGetProfile([], {
    enabled: isLoggedIn,
  });

  const {
    data: locationList,
    refetch: refetchLocations,
    isLoading: isLocationsLoading,
  } = useUserControllerServiceGetAssignedLocations(
    { page: 0, pageSize: 100 },
    [],
    {
      enabled: isLoggedIn,
    },
  );

  useEffect(() => {
    if (isLoggedIn && !previousAuthState.current) {
      dispatch(logout());
      // This reset runs on every page load; keep the navbar provider picks
      // (a real logout purges the persisted store separately).
      dispatch(setPersistedNavbarProviders(persistedNavbarProviders ?? []));
      // Kept for the same reason: a reload must not re-pick a nurse's care
      // team over her own choices, and her locked care team has to be there
      // from the first render.
      dispatch(
        setPersistedNurseDefaultsLocation(
          persistedNurseDefaultsLocation ?? null,
        ),
      );
      dispatch(setPersistedNurseCareTeam(persistedNurseCareTeam ?? []));
      dispatch(setLoadingUserData(true));
      queryClient.clear();
    }
    previousAuthState.current = isLoggedIn;
  }, [
    isLoggedIn,
    dispatch,
    queryClient,
    persistedNavbarProviders,
    persistedNurseDefaultsLocation,
    persistedNurseCareTeam,
  ]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(setLoadingUserData(isProfileLoading || isLocationsLoading));
    } else {
      dispatch(logout());
    }
  }, [isLoggedIn, isProfileLoading, isLocationsLoading, dispatch]);

  useEffect(() => {
    if (isLoggedIn && shouldRefetchProfile) {
      refetch();
      refetchLocations();
    }
  }, [shouldRefetchProfile, isLoggedIn]);

  useEffect(() => {
    const userData: any = data?.data;
    const locations: any = locationList?.data;

    if (isLoggedIn && userData) {
      dispatch(setProfile(userData));
      if (userData?.roleType === "EMPLOYER" || userData?.roleType === "AGENT") {
        dispatch(setStripeCustomerId(userData?.employerCustomerId));
      }
      if (userData.permission) {
        dispatch(setPermissions(userData.permission));
      }
    }

    if (isLoggedIn && userData && locations) {
      dispatch(setProfile(userData));
      dispatch(setAssignedLocations(locations));

      if (userData.permission) {
        dispatch(setPermissions(userData.permission));
      }

      const profileLocation = userData?.locationDetailResponseDto;

      if (profileLocation?.locationUuid) {
        dispatch(setCurrentLocation(profileLocation?.locationUuid));
      } else {
        const currentLocation = locations?.content?.find(
          (location: any) => location?.lastLoggedIn,
        );
        if (currentLocation) {
          dispatch(setCurrentLocation(currentLocation?.locationUuid));
        } else if (locations?.content?.length > 0) {
          dispatch(setCurrentLocation(locations?.content[0]?.locationUuid));
        }
      }
    }
  }, [isLoggedIn, data, locationList, dispatch]);

  const { mutateAsync } =
    usePushNotificationControllerServiceSendFcmNotification({
      onSuccess: (response: any) => {
        dispatch(setIsDeviceRegistered(true));
      },
      onError: (error: any) => {
        dispatch(setIsDeviceRegistered(false));
      },
    });

  const [syncResponse, syncError, syncLoading, syncDeviceToken] = useHttp({
    method: "PUT",
    url: `${BASE_URL}/api/master/sync-device-token`,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  const { mutateAsync: revokeDeviceToken } =
    useUserControllerServiceRevokeDeviceToken();

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.removeItem("isCheckIn");
      localStorage.removeItem("lastRunningTime");
      revokeDeviceToken({
        deviceType: "WEB",
      });
    }
  }, [isLoggedIn, revokeDeviceToken]);

  useEffect(() => {
    const userData: any = data?.data;
    if (isLoggedIn && auth.token) {
      // The profile names the user's uuid differently across role types.
      // Without one, join_user_room is never emitted and the session gets no
      // realtime messaging events at all.
      const userUuid = userData?.userId || userData?.userUuid || userData?.uuid;
      if (!userUuid && userData) {
        console.warn(
          "[Socket] Profile carries no user uuid — realtime events will not be received",
          Object.keys(userData),
        );
      }
      connectSocket(auth.token, userUuid);
    }
  }, [isLoggedIn, auth.token, data]);

  useEffect(() => {
    if (!isLoggedIn) {
      disconnectSocket();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const handleTokenExpired = () => {
      auth
        .updateToken(60)
        .then(() => {
          if (auth.token) {
            connectSocket(auth.token);
          }
        })
        .catch(() => {
          disconnectSocket();
          revokeDeviceToken({
            deviceType: "WEB",
          });
        });
    };

    auth.onTokenExpired = handleTokenExpired;

    return () => {
      auth.onTokenExpired = undefined;
    };
  }, [auth, revokeDeviceToken]);

  useEffect(() => {
    if (auth.authenticated && !isDeviceRegistered) {
      getFCMToken().then((token) => {
        if (token) {
          const formData = new URLSearchParams();
          formData.append("deviceToken", token);
          formData.append("deviceType", "WEB");

          syncDeviceToken(formData.toString());

          mutateAsync({
            requestBody: { token: token },
          });
        }
      });
    }
  }, [auth.authenticated, isDeviceRegistered]);

  useEffect(() => {
    if (!isLoggedIn) {
      auth.login();
    }
  }, [isLoggedIn, auth]);

  if (!isLoggedIn) {
    return (
      <div style={{ height: "100vh", width: "100vw" }}>
        <CenteredLoader />
      </div>
    );
  }

  return (
    <>
      {isLoadingUserData ? (
        <div style={{ height: "100vh", width: "100vw" }}>
          <CenteredLoader />
        </div>
      ) : (
        <>{children}</>
      )}
    </>
  );
}

export default PrivateRoute;
